/**
 * An office's own breakdown format (OfficeBreakdownFormat), fetched once and
 * kept: the template, and its page images.
 *
 *   GET /clinics/breakdown-format/?office_id=<pk>[&format_id=<id>]
 *       → {office_id, format: null | {id, version, is_active, title, template, pages}, versions?}
 *
 * The office's ACTIVE format unless a super admin names a draft (`formatId`).
 * One shared in-flight request per office (and draft), fresh for a minute — the
 * export buttons ask on render and again on click. Page images never change for
 * a version, so they are kept by version and fetched ahead of the click: the
 * export then starts drawing at once.
 *
 * A template of a schema this code doesn't know is ignored (today's exports
 * stand in), and a failed request answers null — never "no format".
 */
import { useEffect, useState } from "react";

import config from "../../../../../../config";

export const TEMPLATE_SCHEMA = 1;
const FRESH_MS = 60 * 1000;
const TIMEOUT_MS = 10 * 1000;
export const OFFICE_FORMAT_EVENT = "office-breakdown-format";

const memory = {};   // "<office>|<format or active>" -> { value, fetchedAt, promise }
const imageMemory = {};  // format id -> Promise<[image]>

function authHeader() {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    return user && user.key ? { Authorization: "Token  " + user.key } : {};
  } catch (e) {
    return {};
  }
}

export function isUsableId(id) {
  return id !== null && id !== undefined && /^\d+$/.test(String(id).trim());
}

function withTimeout(promise) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), TIMEOUT_MS)),
  ]);
}

/** Whitelist the server's answer: {format, versions}. */
export function normalizeFormatPayload(json) {
  const raw = json && typeof json.format === "object" ? json.format : null;
  const template = raw && raw.template;
  const usable = !!(raw && template && template.schema === TEMPLATE_SCHEMA && Array.isArray(template.fields)
    && Array.isArray(raw.pages) && raw.pages.length > 0 && raw.pages.length === (template.pages || []).length);
  return {
    format: usable ? {
      id: raw.id, version: raw.version, isActive: raw.is_active === true, title: raw.title || template.title || "",
      template, pages: raw.pages,
    } : null,
    versions: Array.isArray(json && json.versions)
      ? json.versions.map((v) => ({ id: v.id, version: v.version, isActive: v.is_active === true }))
      : null,
  };
}

async function fetchFormat(officeId, formatId) {
  let url = `${config.apiGateway.URL}/clinics/breakdown-format/?office_id=${encodeURIComponent(officeId)}`;
  if (isUsableId(formatId)) url += `&format_id=${encodeURIComponent(formatId)}`;
  const response = await fetch(new Request(url, { method: "GET", headers: authHeader() }));
  if (response.status !== 200) throw new Error(`status ${response.status}`);
  return normalizeFormatPayload(await response.json());
}

function key(officeId, formatId) {
  return `${officeId}|${isUsableId(formatId) ? formatId : "active"}`;
}

function broadcast(officeId) {
  try {
    window.dispatchEvent(new CustomEvent(OFFICE_FORMAT_EVENT, { detail: { officeId: String(officeId) } }));
  } catch (e) {
    // Consumers still get the value from their own load().
  }
}

/** {format, versions} for the office (its active format, or a super admin's
 * chosen draft). Resolves to null when it couldn't be loaded. */
export function loadOfficeFormat(officeId, { formatId = null, force = false } = {}) {
  if (!isUsableId(officeId)) return Promise.resolve({ format: null, versions: null });
  const k = key(officeId, formatId);
  const entry = memory[k] || {};
  if (entry.promise) return entry.promise;
  if (!force && entry.value && Date.now() - entry.fetchedAt < FRESH_MS) return Promise.resolve(entry.value);
  const promise = withTimeout(fetchFormat(officeId, formatId))
    .then((value) => {
      memory[k] = { value, fetchedAt: Date.now() };
      if (value.format) loadFormatImages(value.format).catch(() => null);   // ahead of the click
      return value;
    })
    .catch(() => {
      // Keep what we knew; with nothing known this reads as failed (and is retried next time).
      memory[k] = { value: entry.value || null, fetchedAt: Date.now() };
      return entry.value || null;
    })
    .then((value) => {
      broadcast(officeId);
      return value;
    });
  memory[k] = { ...entry, promise };
  return promise;
}

/** A page image from a logged-in view (`url` under the API root), as an
 * ImageBitmap where the browser has them, else a loaded <img>. */
export async function fetchProtectedImage(url) {
  const response = await fetch(new Request(`${config.apiGateway.URL}${url}`, { method: "GET", headers: authHeader() }));
  if (!response.ok) throw new Error(`page status ${response.status}`);
  const blob = await response.blob();
  if (typeof window !== "undefined" && typeof window.createImageBitmap === "function") {
    return window.createImageBitmap(blob);
  }
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("page image unreadable"));
    };
    img.src = objectUrl;
  });
}

/** The format's page images, in order (kept per version; a failure is retried next time). */
export function loadFormatImages(format) {
  const id = format && format.id;
  if (!imageMemory[id]) {
    imageMemory[id] = Promise.all((format.pages || []).map((p) => fetchProtectedImage(p.url)));
    imageMemory[id].catch(() => { delete imageMemory[id]; });
  }
  return imageMemory[id];
}

function snapshot(officeId, formatId) {
  const entry = memory[key(officeId, formatId)] || {};
  return {
    ready: !!entry.fetchedAt,
    failed: !!entry.fetchedAt && !entry.value,
    format: (entry.value && entry.value.format) || null,
    versions: (entry.value && entry.value.versions) || null,
  };
}

/** {ready, failed, format, versions} for the export buttons — today's exports
 * until it says otherwise. */
export function useOfficeBreakdownFormat(officeId, formatId = null) {
  const usable = isUsableId(officeId);
  const [state, setState] = useState(() => (usable ? snapshot(officeId, formatId)
    : { ready: true, failed: false, format: null, versions: null }));

  useEffect(() => {
    if (!usable) {
      setState({ ready: true, failed: false, format: null, versions: null });
      return undefined;
    }
    let alive = true;
    const refresh = () => alive && setState(snapshot(officeId, formatId));
    const onEvent = (e) => {
      if (!e || !e.detail || e.detail.officeId === String(officeId)) refresh();
    };
    window.addEventListener(OFFICE_FORMAT_EVENT, onEvent);
    loadOfficeFormat(officeId, { formatId }).then(refresh);
    return () => {
      alive = false;
      window.removeEventListener(OFFICE_FORMAT_EVENT, onEvent);
    };
  }, [officeId, formatId, usable]);

  return state;
}

/** For tests only. */
export function __resetOfficeFormatMemory() {
  Object.keys(memory).forEach((k) => delete memory[k]);
  Object.keys(imageMemory).forEach((k) => delete imageMemory[k]);
}
